import React, { createContext, useContext, useState, useEffect } from 'react';
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { api, setApiRole, BASE } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const checkUser = async () => {
      // If MSAL is still logging in or logging out, we should wait.
      // However, if we are in HandleRedirect and already authenticated, we can proceed.
      const isCriticalInteraction = inProgress === InteractionStatus.Login || 
                                    inProgress === InteractionStatus.Logout;
      


      if (isCriticalInteraction) {
        setLoading(true);
        return;
      }

      if (isAuthenticated && accounts.length > 0) {
        try {

          setLoading(true);
          const account = accounts[0];
          
          // Get token specifically for Microsoft Graph API
          let response;
          try {

            response = await instance.acquireTokenSilent({
              scopes: ["https://graph.microsoft.com/User.Read", "https://graph.microsoft.com/Mail.Send"],
              account: account
            });

          } catch (tokenErr) {

            try {
              response = await instance.acquireTokenPopup({
                scopes: ["https://graph.microsoft.com/User.Read", "https://graph.microsoft.com/Mail.Send"],
                account: account
              });
            } catch (popupErr) {

              throw new Error("Failed to acquire mail permissions. Please sign in again.");
            }
          }

          // Store tokens separately
          localStorage.setItem('auth_token', response.idToken);
          localStorage.setItem('graph_token', response.accessToken);

          // Fetch user details and role from backend with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

          try {
            const res = await fetch(`${BASE}/me`, {
              headers: {
                'Authorization': `Bearer ${response.idToken}`,
                'X-Graph-Token': response.accessToken
              },
              signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!res.ok) {
              let errorMsg = "You are not authorized. Please contact your administrator.";
              try {
                const data = await res.json();
                errorMsg = data.error || errorMsg;
              } catch (jsonErr) {
                errorMsg = `Backend Error (${res.status}): The server might be offline or misconfigured.`;
              }
              throw new Error(errorMsg);
            }

            const userData = await res.json();
            if (userData.role === 'Employee') userData.role = 'Contributor';
            setUser(userData);
            setApiRole(userData.role);
            setError(null);
          } catch (fetchErr) {
            clearTimeout(timeoutId);
            if (fetchErr.name === 'AbortError') {
              throw new Error("Connection to server timed out. Please check your internet or if the backend is running.");
            }
            throw fetchErr;
          }
        } catch (e) {

          setError(e.message);
        } finally {

          setLoading(false);
        }
      } else {
        // Not authenticated and no interaction in progress

        setLoading(false);
        setUser(null);
      }
    };

    checkUser();
  }, [isAuthenticated, accounts, instance, inProgress]);

  const logout = () => {
    localStorage.removeItem('auth_token');
    instance.logoutRedirect();
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, loading, error, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
